import { useRef } from "react";
import type { IChatMessage } from "@/types/(my-space)/chat.interface";

export const messageKey = (m: Pick<IChatMessage, "id" | "clientMessageId">) =>
  String(m.clientMessageId ?? m.id);

interface Seen {
  conversationId: number | null;
  windowVersion: number;
  /** The first page has been recorded (it may be empty: a new chat). */
  seeded: boolean;
  /** Every message key this room has rendered. */
  keys: Set<string>;
  /** Keys that arrived live — they keep the pop-in class for good, so a
   *  re-render mid-animation (my message swapped for its server copy, a read
   *  receipt) doesn't cut it short. CSS runs it once, on mount. */
  fresh: Set<string>;
}

/**
 * The messages that arrived while the room was open (received, or sent by
 * me), as opposed to the first page, older history and catch-up. Only
 * messages newer than everything already shown count: history loads at the
 * top, live messages at the bottom.
 *
 * `windowVersion` changes on every bulk load that isn't live traffic — a
 * jump to an older message, a newer page while detached, re-attaching to the
 * end: whatever the window holds then is marked seen, never fresh, even
 * though it lands below what was shown.
 *
 * Mutates a ref during render on purpose — the update is idempotent, so a
 * StrictMode double render computes the same answer.
 */
export const useFreshMessages = (
  conversationId: number,
  messages: IChatMessage[],
  isLoading: boolean,
  windowVersion = 0,
): ReadonlySet<string> => {
  const ref = useRef<Seen>({
    conversationId: null,
    windowVersion,
    seeded: false,
    keys: new Set(),
    fresh: new Set(),
  });
  const seen = ref.current;

  if (seen.conversationId !== conversationId) {
    seen.conversationId = conversationId;
    seen.seeded = false;
    seen.keys = new Set();
    seen.fresh = new Set();
  }
  // The first page renders as is. Re-arm on every load: switching rooms can
  // render once with the previous room's messages before loading starts.
  if (isLoading) {
    seen.seeded = false;
    seen.keys = new Set();
    return seen.fresh;
  }
  if (seen.windowVersion !== windowVersion) {
    seen.windowVersion = windowVersion;
    seen.seeded = true;
    for (const m of messages) seen.keys.add(messageKey(m));
    return seen.fresh;
  }
  if (!seen.seeded) {
    seen.seeded = true;
    for (const m of messages) seen.keys.add(messageKey(m));
    return seen.fresh;
  }

  // Index of the newest message already shown; anything new after it is live.
  let lastSeen = -1;
  messages.forEach((m, i) => {
    if (seen.keys.has(messageKey(m))) lastSeen = i;
  });
  messages.forEach((m, i) => {
    const key = messageKey(m);
    if (seen.keys.has(key)) return;
    seen.keys.add(key);
    if (i > lastSeen && m.type !== "system") seen.fresh.add(key);
  });
  return seen.fresh;
};

